import { Link } from "react-router-dom";
import { SEOHead } from "@/components/SEOHead";
import { Header } from "@/components/layout/Header";
import { BlogBreadcrumb } from "@/components/seo/BlogBreadcrumb";
import { Footer } from "@/components/layout/Footer";
import { SEOContentBlock } from "@/components/seo/SEOContentBlock";
import { RelatedReads } from "@/components/seo/RelatedReads";
import { getRelatedPosts } from "@/content/blogPosts";
import { Button } from "@/components/ui/button";
import { ArrowLeft, CalendarDays } from "lucide-react";
import heroImage from "@/assets/gallery/sons-tubes-stack.jpg";
import friendsImg from "@/assets/tubing/friends-floating-cooler.jpg";
import tubesImg from "@/assets/tubing/sons-tubes-stacked.jpg";
import canyonImg from "@/assets/tubing/canyon-tubing-scene.jpg";

const PUBLISH_DATE = "May 5, 2026";
const CANONICAL = "/blog/guadalupe-river-tube-rental-near-new-braunfels";
const TITLE =
  "Guadalupe River Tube Rental, New Braunfels | Son's";
const DESCRIPTION =
  "Guadalupe River tube rental near New Braunfels: prices, what's included, how it works & why Son's beats the crowded outfitter scene.";

const FAQS = [
  {
    q: "How much does a Guadalupe River tube rental cost near New Braunfels?",
    a: "Tube rentals at Son's Guadalupe start at $24.99 for all-day tubing. Kayak add-ons and double tubes are available, and we frequently run weekday and Sunday Funday specials.",
  },
  {
    q: "What's included with a tube rental?",
    a: "Your rental includes an all-day single-tube rental on the Guadalupe River, river access from your Son's property, on-site restrooms and picnic areas, and free parking when paired with a cabana booking.",
  },
  {
    q: "Do I need a shuttle to go tubing?",
    a: "No. Unlike traditional outfitters, you float directly from your Son's Guadalupe property — no school bus shuttle, no giant parking lot, no separate outfitter to deal with.",
  },
  {
    q: "Should I book a cabana with my tube rental?",
    a: "We recommend it. Pairing tubes with a cabana on the Guadalupe River gives you a shaded basecamp, free parking, a picnic table, and easy in-and-out floats all day.",
  },
  {
    q: "Can I rent kayaks too?",
    a: "Yes — kayak rentals are available on most properties and can be added to your tubing reservation when you book online.",
  },
];

const schemaJson = {
  "@context": "https://schema.org",
  "@graph": [
    {
      "@type": "BreadcrumbList",
      itemListElement: [
        { "@type": "ListItem", position: 1, name: "Home", item: "https://sonsguadalupe.com/" },
        { "@type": "ListItem", position: 2, name: "Blog", item: "https://sonsguadalupe.com/blog" },
        { "@type": "ListItem", position: 3, name: "Guadalupe River Tube Rental Near New Braunfels — Prices & How It Works", item: `https://sonsguadalupe.com/blog/guadalupe-river-tube-rental-near-new-braunfels` },
      ],
    },
    {
      "@type": "BlogPosting",
      headline: "Guadalupe River Tube Rental Near New Braunfels — Prices & How It Works",
      description: DESCRIPTION,
      image: "https://sonsguadalupe.com/og-image.png",
      author: { "@type": "Organization", name: "Son's Guadalupe" },
      datePublished: "2026-05-05",
      mainEntityOfPage: { "@type": "WebPage", "@id": `https://sonsguadalupe.com${CANONICAL}` },
      keywords:
        "Guadalupe River tube rental near New Braunfels, tube rental New Braunfels, tubing the Guadalupe River, kayak rental New Braunfels, cabana rentals on the Guadalupe River",
    },
    {
      "@type": "FAQPage",
      mainEntity: FAQS.map(({ q, a }) => ({
        "@type": "Question",
        name: q,
        acceptedAnswer: { "@type": "Answer", text: a },
      })),
    },
  ],
};

const BlogTubeRentalNewBraunfels = () => {
  const related = getRelatedPosts("guadalupe-river-tube-rental-near-new-braunfels", 3);
  return (
    <div className="min-h-screen">
      <SEOHead title={TITLE} description={DESCRIPTION} canonical={CANONICAL} schemaJson={schemaJson} />
      <Header />
      <BlogBreadcrumb />
      <main className="pt-24 pb-16">
        <div className="relative h-[55vh] min-h-[400px]">
          <img fetchPriority="high" loading="eager" decoding="async" src={heroImage} alt="Stacked Son's Guadalupe tubes — tube rental near New Braunfels" className="absolute inset-0 w-full h-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/30 to-transparent" />
          <div className="absolute bottom-0 left-0 right-0 p-6 md:p-12">
            <div className="container-narrow mx-auto">
              <div className="flex items-center gap-2 text-white/80 text-sm mb-3">
                <CalendarDays className="w-4 h-4" /> {PUBLISH_DATE}
              </div>
              <h1 className="font-heading text-3xl md:text-5xl font-bold text-white max-w-4xl leading-tight">
                Guadalupe River Tube Rental Near New Braunfels — Prices & How It Works
              </h1>
            </div>
          </div>
        </div>

        <article className="section-padding bg-background">
          <div className="container-narrow mx-auto max-w-3xl px-4">
            <Link to="/blog" className="inline-flex items-center gap-2 text-sm text-primary hover:underline mb-8">
              <ArrowLeft className="w-4 h-4" /> Back to Blog
            </Link>

            <div className="prose prose-lg max-w-none text-foreground space-y-6">
              <p className="text-xl leading-relaxed">
                Looking for the easiest <strong>Guadalupe River tube rental near New Braunfels</strong>? You're in the right place. Son's Guadalupe runs riverside properties between New Braunfels and Seguin where you can rent tubes, kayaks, and a private cabana — all in one booking, no shuttle stress, no parking-lot chaos.
              </p>

              <img src={friendsImg} alt="Friends floating with tubes and a cooler on the Guadalupe River near New Braunfels" loading="lazy" className="w-full rounded-2xl shadow-elevated my-8" />

              <h2 className="font-heading text-2xl font-bold text-foreground mt-10">How Much Does a Guadalupe River Tube Rental Cost?</h2>
              <p>
                Tube rentals at Son's Guadalupe start at <strong>$24.99 for all-day tubing</strong>, with kayak add-ons available. We frequently run weekday and Sunday Funday specials — keep an eye on the banner at the top of the site for current promos. Pricing is per tube, per day, and includes river access from your Son's property.
              </p>

              <h2 className="font-heading text-2xl font-bold text-foreground mt-10">What's Included With Your Tube Rental?</h2>
              <ul className="list-disc pl-6 space-y-2">
                <li>All-day single-tube rental on the Guadalupe River</li>
                <li>River access from a Son's property — no separate outfitter to deal with</li>
                <li>On-site restrooms, picnic areas, and friendly staff</li>
                <li>Optional cooler tubes, double tubes, and kayak upgrades</li>
                <li>Free parking when paired with a cabana booking</li>
              </ul>

              <img src={tubesImg} alt="Son's Guadalupe tubes stacked at the river — tube rental near New Braunfels" loading="lazy" className="w-full rounded-2xl shadow-elevated my-8" />

              <h2 className="font-heading text-2xl font-bold text-foreground mt-10">How Tube Rental Works at Son's Guadalupe</h2>
              <ol className="list-decimal pl-6 space-y-2">
                <li><strong>Book online.</strong> Reserve tubes (and a cabana, if you want one) ahead of time on our Book Now page.</li>
                <li><strong>Arrive at your Son's property.</strong> Check in, grab your tubes, and walk to the water.</li>
                <li><strong>Float the family-friendly stretch.</strong> Our staff will point you to the put-in and pickup spots.</li>
                <li><strong>Repeat.</strong> All-day means you can float as many times as you want.</li>
              </ol>

              <h2 className="font-heading text-2xl font-bold text-foreground mt-10">Why Rent Through Son's Instead of a Traditional Outfitter?</h2>
              <p>
                Most tube rentals near New Braunfels mean parking in a giant lot, riding a school bus to the put-in, and hoping your group stays together. At Son's Guadalupe, your tube rental is paired with a private property — so you float right from your cabana or campsite. It's the calmer, more family-friendly way to do tubing the Guadalupe River.
              </p>

              <img src={canyonImg} alt="Cypress canyon stretch of the Guadalupe River — tube rental scene" loading="lazy" className="w-full rounded-2xl shadow-elevated my-8" />

              <h2 className="font-heading text-2xl font-bold text-foreground mt-10">Bundle Your Tube Rental With a Cabana</h2>
              <p>
                Our most popular package: <strong>cabana rentals on the Guadalupe River</strong> + tubes for the group. The cabana gives you a shaded basecamp, free parking, and a picnic table — the tubes give you the float. Add glamping or tent camping on River Road and you've got a full weekend.
              </p>

              <h2 className="font-heading text-2xl font-bold text-foreground mt-10">Tube Rental FAQs</h2>
              <div className="space-y-6">
                {FAQS.map((f) => (
                  <div key={f.q}>
                    <h3 className="font-heading text-lg font-semibold text-foreground">{f.q}</h3>
                    <p className="mt-2">{f.a}</p>
                  </div>
                ))}
              </div>

              <div className="flex flex-col sm:flex-row gap-4 justify-center mt-10">
                <Link to="/book-now"><Button variant="default" size="lg" className="text-lg px-10 py-6">Reserve Tubes</Button></Link>
                <Link to="/tubing"><Button variant="outline" size="lg" className="text-lg px-10 py-6">Tubing Details</Button></Link>
              </div>
            </div>
          </div>
        </article>
        <RelatedReads posts={related} />

      </main>
      <SEOContentBlock
        eyebrow="More Tubing Resources"
        heading="Plan Your Guadalupe River Tubing Day"
        paragraphs={[
          "Renting tubes is just the start. Add a cabana rental on the Guadalupe River, kayaks, glamping near New Braunfels, or tent camping on River Road to turn a tube rental day into a full Guadalupe River getaway.",
        ]}
        links={[
          { label: "Tubing Info", to: "/tubing" },
          { label: "Cabanas", to: "/cabanas" },
          { label: "Glamping", to: "/glamping" },
          { label: "Book Now", to: "/book-now" },
        ]}
      />
      <Footer />
    </div>
  );
};

export default BlogTubeRentalNewBraunfels;
